import {
  IconMovie,
  IconPresentation,
  IconFileCheck,
  IconChartArrows,
  IconArrowAutofitLeft,
  IconMessage,
  IconUser,
  IconNotes,
  IconPlayerPlay,
  IconPlayerStop
} from '@tabler/icons-vue';
import { nextTick } from 'vue';

import { CUE_ACTION, CUE_PROPERTY_TYPES, SESSION_TYPE, THIRTY_SECONDS, SESSION_ACTIONS } from '../../config/constants';
import {
  trackAnalytics,
  getCurrentBroadcast,
  getRouteInfo,
  clearStageItemBeforePublishing,
  getCueProperty,
  getSurveyIdentity,
  isSurveyCueScheduled
} from '../../utils/commonUtils';

import type { LiveRunOfShowCue } from '@/store/modules/event/types';

import logger from '@/services/logger.service';
import { handleSpeakerActivity, ActivityType } from '@/services/speakerActivity.service';
import store from '@/store';
import { BroadcastActions } from '@/store/modules/user/types';
import { timeDiff } from '@/utils/dateTime';

function isPermitted(broadcastActionConstant: keyof typeof BroadcastActions) {
  const broadcast = getCurrentBroadcast();
  return store.getters.isBroadcastActionPermitted(BroadcastActions[broadcastActionConstant], broadcast?.id);
}

const createSessionExecutor = (actionType: 'start' | 'end', analyticsEvent: string) => {
  return (cue: LiveRunOfShowCue) => {
    const { broadcastId } = getRouteInfo();
    if (!broadcastId) {
      logger.error(`No broadcast ID available for ${actionType}ing session`);
      return;
    }

    const actionMap = {
      start: 'startManualSessionAction',
      end: 'endManualSessionAction'
    };

    store.dispatch(`event/${actionMap[actionType]}`, {
      sessionId: broadcastId,
      sessionType: SESSION_TYPE.BROADCAST
    });

    trackAnalytics(analyticsEvent, { sessionId: broadcastId });
  };
};

const executeShareVideo = async (cue: LiveRunOfShowCue) => {
  const broadcast = getCurrentBroadcast();
  const videoId = getCueProperty(cue, CUE_PROPERTY_TYPES.VIDEO);

  // Force override whatever is on stage (including HLS which may not be visibleOnStage)
  const publishedStage = store.getters['sidebar/publishedStage'];
  if (publishedStage?.currentScreen === 'poll') {
    await clearStageItemBeforePublishing();
  }

  if (broadcast?.broadcast_type === 'LIVE_HYBRID') {
    await store.dispatch('liveVideoRoom/switchToVideoMode', videoId);
  }

  trackAnalytics('video_played_on_stage', { videoId });
};

const executeSeedChatMessage = (cue: LiveRunOfShowCue) => {
  const message = getCueProperty(cue, CUE_PROPERTY_TYPES.MESSAGE);

  const currentSubTabIndex = store.state.sidebar.stageChatSubTabIndex;
  const stageChatTabs = store.getters['sidebar/stageChatTabs'];
  const channel_id = stageChatTabs[currentSubTabIndex]?.channel();

  if (!channel_id) {
    logger.error('No active chat channel found for seeding message');
    return;
  }

  store.dispatch('event/avroom/postMessage', {
    message,
    channel_id
  });

  trackAnalytics('chat_message_seeded_from_cue');

  store.dispatch('event/updateLiveRunOfShowCue', {
    cueId: cue.id,
    updates: { status: 'COMPLETED' }
  });
};

const executePollManagement = async (cue: LiveRunOfShowCue) => {
  const { pollId, isOpen } = getPollInfo(cue);

  if (!pollId) {
    return;
  }

  if (cue.status === 'ACTIVE' || isOpen) {
    await handleSpeakerActivity(
      () => store.dispatch('event/avroom/closeAllOpenedPolls'),
      ActivityType.PollClose,
      ActivityType.PollCloseFailed
    );

    trackAnalytics('poll_closed_from_cue', { pollId });
  } else {
    await handleSpeakerActivity(
      () => store.dispatch('event/avroom/openPollForPolling', pollId),
      ActivityType.PollOpen,
      ActivityType.PollOpenFailed
    );

    trackAnalytics('poll_opened_from_cue', { pollId });
  }
};

const executePollSharing = async (cue: LiveRunOfShowCue) => {
  const { pollId, isSharing } = getPollInfo(cue);

  if (!pollId) {
    logger.error('No poll ID available for poll sharing');
    return;
  }

  if (cue.status === 'ACTIVE' || isSharing) {
    await handleSpeakerActivity(
      () => store.dispatch('event/avroom/unPublishPoll'),
      ActivityType.PollUnShare,
      ActivityType.PollUnShareFailed
    );

    trackAnalytics('poll_unshared_from_cue', { pollId });
  } else {
    const publishedStage = store.getters['sidebar/publishedStage'];
    if (publishedStage?.currentScreen === 'live_hls') {
      await clearStageItemBeforePublishing();
    }

    await handleSpeakerActivity(
      () => store.dispatch('event/avroom/publishPoll', pollId),
      ActivityType.PollShare,
      ActivityType.PollShareFailed
    );

    trackAnalytics('poll_shared_from_cue', { pollId });
  }
};

const executeShareSlide = async (cue: LiveRunOfShowCue) => {
  const slideId = getCueProperty(cue, CUE_PROPERTY_TYPES.SLIDE);
  const slideNumber = getCueProperty(cue, CUE_PROPERTY_TYPES.SLIDE_NUMBER);
  if (!slideId) {
    logger.error('No slide ID available for slide sharing');
    return;
  }

  // Complete other active slide cues before sharing new slide
  const allCues = store.state.event.liveRunOfShow?.cues || [];
  const otherActiveSlideCues = allCues.filter(
    otherCue =>
      otherCue.id !== cue.id && otherCue.status === 'ACTIVE' && getCueProperty(otherCue, CUE_PROPERTY_TYPES.SLIDE)
  );

  for (const otherCue of otherActiveSlideCues) {
    await store.dispatch('event/updateLiveRunOfShowCue', {
      cueId: otherCue.id,
      updates: { status: 'COMPLETED' }
    });
  }

  const deck = store.state.sidebar.slideDecks.slides?.[slideId];
  if (!deck) {
    try {
      await store.dispatch('sidebar/loadDeck', { asset: slideId });
    } catch (e) {
      logger.error('Failed to load slide deck before sharing', e);
      return;
    }
  }

  const slideNumberValue = slideNumber ? parseInt(slideNumber, 10) - 1 : 0;
  const slideTitle = deck?.name || 'Unknown Slide';

  const publishedStage = store.getters['sidebar/publishedStage'];
  if (publishedStage?.currentScreen && publishedStage?.currentScreen !== 'slide') {
    await clearStageItemBeforePublishing();
    await nextTick();
  }

  // Handle speaker activity for slide sharing
  await handleSpeakerActivity(
    () =>
      store.dispatch('sidebar/setSlidePage', {
        slide: slideId,
        page: slideNumberValue
      }),
    ActivityType.SlideShareStart,
    ActivityType.SlideShareFailed,
    {
      title: slideTitle,
      slideNumber: slideNumberValue
    }
  );

  trackAnalytics('slide_shared_from_cue', {
    slideId,
    slideNumber: slideNumberValue + 1
  });
};

const executeStartSession = createSessionExecutor('start', 'session_started_from_cue');
const executeEndSession = createSessionExecutor('end', 'session_ended_from_cue');

// Info Getters
const getSurveyInfo = (cue: LiveRunOfShowCue) => {
  const surveyId = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY);
  if (!surveyId) {
    return { survey: null, isPublished: false, isSurveyClosed: false };
  }

  const surveys = store.getters['event/survey/getAllSurveys'] || [];
  const survey = surveys.find((s: any) => s.id === surveyId);
  const publishedSurveyId = store.state.event?.survey?.publishedSurveyId;
  const isSurveyClosed = store.state.event?.survey?.surveys[surveyId]?.surveyStatus === 'CLOSING';

  return {
    survey,
    isPublished: survey?.id === publishedSurveyId,
    isSurveyClosed
  };
};

const getVideoInfo = (cue: LiveRunOfShowCue) => {
  const videoId = getCueProperty(cue, CUE_PROPERTY_TYPES.VIDEO);
  if (!videoId) {
    return { video: null, videoId: null, isSharing: false };
  }

  const broadcast = getCurrentBroadcast();
  const video = broadcast?.video_assets?.find((v: any) => v.id === videoId);
  const publishedStage = store.getters['sidebar/publishedStage'];
  const isVideoPublished = publishedStage?.params?.id === videoId;

  return { video, videoId, isVideoPublished };
};

const getPollInfo = (cue: LiveRunOfShowCue) => {
  const pollId = getCueProperty(cue, CUE_PROPERTY_TYPES.POLL);
  if (!pollId) {
    return { poll: null, pollId: null, isOpen: false, isSharing: false };
  }

  const pollDetails = store.state.event?.avroom?.polls?.details;
  const poll = pollDetails?.questions?.[pollId];
  const isOpen = pollDetails?.currentOpenedPollID === pollId;
  const isSharing = pollDetails?.currentPublishedPollID === pollId;
  return { poll, pollId, isOpen, isSharing };
};

const getSessionInfo = () => {
  const broadcast = getCurrentBroadcast();
  const now = store.state.event.stage?.heartBeatDate || new Date();

  const allowedStartTime = broadcast?.allowed_start_time ? new Date(broadcast.allowed_start_time) : null;
  const allowedEndTime = broadcast?.allowed_end_time ? new Date(broadcast.allowed_end_time) : null;

  const isWithinAllowedTimeWindow =
    allowedStartTime && allowedEndTime ? now >= allowedStartTime && now < allowedEndTime : true;

  return {
    broadcast,
    isLive: broadcast?.is_live,
    isStartDisabled: broadcast?.agenda_control_loading_state?.start_button,
    isEndDisabled: broadcast?.agenda_control_loading_state?.end_button,
    isWithinAllowedTimeWindow,
    now
  };
};

function getSurveySubLabel(cue: LiveRunOfShowCue): string | { key: string; values?: Record<string, any> } {
  const { survey } = getSurveyInfo(cue);

  if (cue.status === 'ACTIVE' && survey?.timeLeftInClosingSurvey) {
    return { key: CUE_ACTION.TIME_LEFT, values: { time: survey.timeLeftInClosingSurvey } };
  }

  if (cue.status !== 'COMPLETED' && isSurveyCueScheduled(cue)) {
    const openTime = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_OPEN_TIME);
    const duration = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_DURATION);
    const launchType = getCueProperty(cue, CUE_PROPERTY_TYPES.SURVEY_LAUNCH_ENUM);

    const hasOpenAndDuration =
      typeof openTime === 'string' && typeof duration === 'string' && openTime.trim() !== '' && duration.trim() !== '';

    if (hasOpenAndDuration && launchType && launchType.trim() !== '') {
      return '@@translate@@ Scheduled';
    }

    return '';
  }

  return '';
}

export const cueListConfig: CueConfig[] = [
  {
    id: 'shareVideo',
    cueTitle: CUE_ACTION.VIDEO,
    icon: IconMovie,
    component: 'VideoCue',
    executeAction: (cue: LiveRunOfShowCue) => (cue.status === 'ACTIVE' ? CUE_ACTION.STOP_SHARING : CUE_ACTION.SHARE),
    execute: async (cue: LiveRunOfShowCue) => {
      const { isVideoPublished, videoId } = getVideoInfo(cue);
      if (isVideoPublished) {
        store.dispatch('sidebar/unpublish');
        trackAnalytics('video_sharing_stopped_from_cue', { videoId });
      } else {
        try {
          await executeShareVideo(cue);
          await store.dispatch('event/updateLiveRunOfShowCue', {
            cueId: cue.id,
            updates: { status: 'ACTIVE' }
          });
        } catch (error) {
          logger.error('Failed to execute video cue', error);
        }
      }
    },
    isEditable: true,
    editableProperties: [CUE_PROPERTY_TYPES.VIDEO],
    isDisabled: (cue: LiveRunOfShowCue) => cue.status === 'COMPLETED',
    isActive: (cue: LiveRunOfShowCue) => cue.status === 'ACTIVE',
    isPermitted: () => isPermitted('START_STOP_VIDEO')
  },

  {
    id: 'shareSlides',
    cueTitle: CUE_ACTION.SHARE_SLIDES,
    icon: IconPresentation,
    component: 'SlideShareCue',
    executeAction: (cue: LiveRunOfShowCue) => (cue.status === 'ACTIVE' ? CUE_ACTION.STOP_SHARING : CUE_ACTION.SHARE),
    execute: async (cue: LiveRunOfShowCue) => {
      const slideId = getCueProperty(cue, CUE_PROPERTY_TYPES.SLIDE);

      const publishedStage = store.getters['sidebar/publishedStage'];
      const isCurrentSlide = publishedStage?.params?.id === slideId;

      if (cue.status === 'ACTIVE') {
        if (isCurrentSlide) {
          await store.dispatch('sidebar/unpublish');
        }

        await store.dispatch('event/updateLiveRunOfShowCue', {
          cueId: cue.id,
          updates: { status: 'COMPLETED' }
        });
        return;
      }

      try {
        await executeShareSlide(cue);
        await store.dispatch('event/updateLiveRunOfShowCue', {
          cueId: cue.id,
          updates: { status: 'ACTIVE' }
        });
      } catch (error) {
        logger.error('Failed to execute slide cue', error);
      }
    },
    isEditable: true,
    editableProperties: [CUE_PROPERTY_TYPES.SLIDE, CUE_PROPERTY_TYPES.SLIDE_NUMBER],
    isDisabled: (cue: LiveRunOfShowCue, isExecuting = false) => {
      const publishedStage = store.getters['sidebar/publishedStage'];
      const currentScreen = publishedStage?.currentScreen;
      return isExecuting || cue.status === 'COMPLETED' || ['screen', 'broadcastOverlay'].includes(currentScreen);
    },

    isActive: (cue: LiveRunOfShowCue) => cue.status === 'ACTIVE',
    isPermitted: () => isPermitted('CONTROL_SLIDES')
  },
  {
    id: 'survey',
    cueTitle: CUE_ACTION.SURVEY,
    icon: IconFileCheck,
    component: 'SurveyCue',
    subLabel: (cue: LiveRunOfShowCue) => getSurveySubLabel(cue),
    executeAction: (cue: LiveRunOfShowCue) => (cue.status === 'ACTIVE' ? CUE_ACTION.CLOSE : CUE_ACTION.LAUNCH),
    execute: async (cue: LiveRunOfShowCue) => {
      const { survey, isPublished } = getSurveyInfo(cue);
      if (!survey || (isPublished && survey.timeLeftInClosingSurvey)) {
        return;
      }

      const action = isPublished ? 'unPublishSurveyDialog' : 'showPublishSurveyDialog';
      const analyticsEvent = isPublished ? 'survey_closed_from_cue' : 'survey_launched_from_cue';
      const surveyIdentity = getSurveyIdentity(cue);

      if (isPublished) {
        store.commit('event/setActiveCueId', null);
      } else {
        store.commit('event/setActiveCueId', surveyIdentity);
      }

      await store.dispatch(`event/survey/${action}`, survey.id);
      trackAnalytics(analyticsEvent, { surveyId: survey.id });
    },
    isEditable: true,
    editableProperties: [
      CUE_PROPERTY_TYPES.SURVEY,
      CUE_PROPERTY_TYPES.SURVEY_OPEN_TIME,
      CUE_PROPERTY_TYPES.SURVEY_DURATION,
      CUE_PROPERTY_TYPES.SURVEY_LAUNCH_ENUM,
      CUE_PROPERTY_TYPES.SURVEY_TOGGLE
    ],
    isDisabled: (cue: LiveRunOfShowCue) => {
      const { survey, isPublished } = getSurveyInfo(cue);
      return isPublished && !!survey?.timeLeftInClosingSurvey;
    },
    isActive: (cue: LiveRunOfShowCue) => cue.status === 'ACTIVE',
    isPermitted: () => isPermitted('LAUNCH_SURVEY')
  },
  {
    id: 'pollManagement',
    cueTitle: CUE_ACTION.OPEN_POLL,
    icon: IconChartArrows,
    component: 'PollManagementCue',
    executeAction: (cue: LiveRunOfShowCue) => (cue.status === 'ACTIVE' ? CUE_ACTION.CLOSE : CUE_ACTION.OPEN_POLL),
    execute: async (cue: LiveRunOfShowCue) => {
      const { pollId } = getPollInfo(cue);
      if (!pollId) {
        return;
      }

      await executePollManagement(cue);

      const updates = cue.status === 'ACTIVE' ? { status: 'COMPLETED' } : { status: 'ACTIVE' };
      await store.dispatch('event/updateLiveRunOfShowCue', {
        cueId: cue.id,
        updates
      });
    },
    isEditable: true,
    editableProperties: [CUE_PROPERTY_TYPES.POLL],
    isDisabled: (cue: LiveRunOfShowCue) => {
      const { pollId } = getPollInfo(cue);
      const currentOpenedPollID = store.state.event?.avroom?.polls?.details?.currentOpenedPollID;
      const anotherPollOpen = !!currentOpenedPollID && pollId !== currentOpenedPollID;

      return !pollId || cue.status === 'COMPLETED' || anotherPollOpen;
    },
    isActive: (cue: LiveRunOfShowCue) => cue.status === 'ACTIVE',
    isPermitted: () => {
      return store.getters['event/avroom/isPollActionPermitted'](BroadcastActions.CONTROL_POLLS);
    }
  },
  {
    id: 'pollSharing',
    cueTitle: CUE_ACTION.SHARE_POLL,
    icon: IconChartArrows,
    component: 'PollManagementCue',
    executeAction: (cue: LiveRunOfShowCue) => (cue.status === 'ACTIVE' ? CUE_ACTION.STOP_SHARING : CUE_ACTION.SHARE),
    execute: async (cue: LiveRunOfShowCue) => {
      const { pollId } = getPollInfo(cue);
      if (!pollId) {
        return;
      }

      await executePollSharing(cue);

      const updates = cue.status === 'ACTIVE' ? { status: 'COMPLETED' } : { status: 'ACTIVE' };
      await store.dispatch('event/updateLiveRunOfShowCue', {
        cueId: cue.id,
        updates
      });
    },
    isEditable: true,
    editableProperties: [CUE_PROPERTY_TYPES.POLL],
    isDisabled: (cue: LiveRunOfShowCue) => {
      const { pollId } = getPollInfo(cue);
      const publishedStage = store.getters['sidebar/publishedStage'];
      const nonOverridableContent = ['screen', 'broadcastOverlay'].includes(publishedStage?.currentScreen);
      return !pollId || cue.status === 'COMPLETED' || nonOverridableContent;
    },
    isActive: (cue: LiveRunOfShowCue) => cue.status === 'ACTIVE',
    isPermitted: () => {
      return store.getters['event/avroom/isPollActionPermitted'](BroadcastActions.SHARE_POLLS);
    }
  },
  {
    id: 'shareTicker',
    cueTitle: CUE_ACTION.SHARE_TICKER,
    icon: IconArrowAutofitLeft,
    component: 'TickerCue',
    executeAction: (cue: LiveRunOfShowCue) =>
      cue.status === 'ACTIVE' ? CUE_ACTION.STOP_TICKER : CUE_ACTION.SHARE_TICKER,
    execute: async (cue: LiveRunOfShowCue, componentRef?: any) => {
      const broadcast = getCurrentBroadcast();
      const message = getCueProperty(cue, CUE_PROPERTY_TYPES.TICKER_MESSAGE);
      const isActive = broadcast?.show_ticker && broadcast?.ticker_text === message;
      const actionType = isActive ? CUE_ACTION.STOP_TICKER : CUE_ACTION.SHARE_TICKER;

      try {
        await componentRef.executeTickerAction(actionType);

        if (actionType === CUE_ACTION.STOP_TICKER) {
          await store.dispatch('event/updateLiveRunOfShowCue', {
            cueId: cue.id,
            updates: { status: 'COMPLETED' }
          });
        } else {
          await store.dispatch('event/updateLiveRunOfShowCue', {
            cueId: cue.id,
            updates: { status: 'ACTIVE' }
          });
        }
      } catch (error) {
        logger.error('Failed to execute ticker cue', error);
      }
    },
    isEditable: true,
    editableProperties: [CUE_PROPERTY_TYPES.TICKER_MESSAGE, CUE_PROPERTY_TYPES.TICKER_URL],
    isActive: (cue: LiveRunOfShowCue) => cue.status === 'ACTIVE',
    isDisabled: (cue: LiveRunOfShowCue) => cue.status === 'COMPLETED',
    isPermitted: () => isPermitted('CONTROL_SESSION_TICKER')
  },
  {
    id: 'seedChatMessage',
    cueTitle: CUE_ACTION.SEED_CHAT_MESSAGE,
    icon: IconMessage,
    component: 'SeedChatMessageCue',
    executeAction: CUE_ACTION.SEND_TO_CHAT,
    execute: executeSeedChatMessage,
    isEditable: true,
    editableProperties: [CUE_PROPERTY_TYPES.MESSAGE],
    isDisabled: (cue: LiveRunOfShowCue) => cue.status === 'COMPLETED',
    isPermitted: () => isPermitted('CONTROL_CHAT')
  },
  {
    id: 'moveToStage',
    cueTitle: CUE_ACTION.MOVE_TO_STAGE,
    icon: IconUser,
    component: 'MoveToStageCue',
    executeAction: CUE_ACTION.MOVE_TO_STAGE,
    execute: async (cue: LiveRunOfShowCue, componentRef?: any) => {
      await componentRef.executeMoveToStageAction();
    },
    isEditable: true,
    isDisabled: (cue: LiveRunOfShowCue) => cue.status === 'COMPLETED',
    isPermitted: () => isPermitted('MOVE_SPEAKERS_STAGE')
  },
  {
    id: 'moveOffStage',
    cueTitle: CUE_ACTION.MOVE_OFF_STAGE,
    icon: IconUser,
    component: 'MoveToStageCue',
    executeAction: CUE_ACTION.MOVE_OFF_STAGE,
    execute: async (cue: LiveRunOfShowCue, componentRef?: any) => {
      await componentRef.executeMoveOffStageAction();
    },
    isEditable: true,
    isDisabled: (cue: LiveRunOfShowCue) => cue.status === 'COMPLETED',
    isPermitted: () => isPermitted('MOVE_SPEAKERS_STAGE')
  },
  {
    id: 'stageDirection',
    cueTitle: CUE_ACTION.STAGE_DIRECTION,
    icon: IconNotes,
    component: 'StageDirectionCue',
    executeAction: CUE_ACTION.DONE,
    execute: async (cue: LiveRunOfShowCue) => {
      await store.dispatch('event/updateLiveRunOfShowCue', {
        cueId: cue.id,
        updates: { status: 'COMPLETED' }
      });

      trackAnalytics('stage_direction_posted_from_cue');
    },
    isEditable: true,
    editableProperties: [CUE_PROPERTY_TYPES.MESSAGE],
    isPermitted: () => isPermitted('CONTROL_STAGE_DIRECTION')
  },
  {
    id: 'sessionStart',
    cueTitle: CUE_ACTION.START_SESSION,
    icon: IconPlayerPlay,
    component: null,
    executeAction: CUE_ACTION.START_SESSION,
    execute: executeStartSession,
    isDisabled: () => {
      const { isStartDisabled, isWithinAllowedTimeWindow, broadcast, now } = getSessionInfo();
      if (isStartDisabled || broadcast?.is_live || !isWithinAllowedTimeWindow) {
        return true;
      }
      if (broadcast?.live_start_time) {
        const timeLeftToStart = timeDiff(now, broadcast.live_start_time);
        return timeLeftToStart > 0 && timeLeftToStart <= THIRTY_SECONDS;
      }
      return false;
    },
    isPermitted: () => isPermitted('START_STOP_MANUAL_MODE_SESSIONS')
  },
  {
    id: 'sessionEnd',
    cueTitle: CUE_ACTION.END_SESSION,
    icon: IconPlayerStop,
    component: null,
    executeAction: CUE_ACTION.END_SESSION,
    execute: executeEndSession,
    isDisabled: () => {
      const broadcast = store.getters['event/currentBroadcast'];
      return !broadcast?.is_live || !broadcast?.manual_redirection_enabled;
    },
    isPermitted: () => isPermitted('START_STOP_MANUAL_MODE_SESSIONS')
  }
];

const ACTION_CONFIG_MAP: Record<string, string> = {
  SHARE_VIDEO: 'shareVideo',
  SHARE_SLIDE: 'shareSlides',
  LAUNCH_SURVEY: 'survey',
  CLOSE_SURVEY: 'survey',
  OPEN_POLL: 'pollManagement',
  SHARE_POLL: 'pollSharing',
  SHARE_TICKER: 'shareTicker',
  SEED_CHAT_MESSAGE: 'seedChatMessage',
  MOVE_SPEAKERS_TO_STAGE: 'moveToStage',
  MOVE_SPEAKERS_OFF_STAGE: 'moveOffStage',
  STAGE_DIRECTION: 'stageDirection',
  [SESSION_ACTIONS.START]: 'sessionStart',
  [SESSION_ACTIONS.END]: 'sessionEnd'
};

export const getCueConfigByAction = (action: string): CueConfig | undefined => {
  const configId = ACTION_CONFIG_MAP[action];
  return configId ? cueListConfig.find(config => config.id === configId) : undefined;
};

export type ExecuteAction = string | ((cue: LiveRunOfShowCue) => string);
export type SubLabel = string | ((cue: LiveRunOfShowCue) => string | { key: string; values?: Record<string, unknown> });
export type ExecuteFn = (cue: LiveRunOfShowCue, componentRef?: any) => void | Promise<void>;
export type IsDisabledFn = ((cue: LiveRunOfShowCue, isExecuting?: boolean) => boolean) | (() => boolean);
export type IsActiveFn = (cue: LiveRunOfShowCue) => boolean;

export interface CueConfig {
  id: string;
  cueTitle: string;
  icon: any;
  component: any;
  executeAction: ExecuteAction;
  execute: ExecuteFn;
  isDisabled?: IsDisabledFn;
  isActive?: IsActiveFn;
  subLabel?: SubLabel;
  isEditable?: boolean;
  editableProperties?: string[];
  isPermitted?: (cue?: LiveRunOfShowCue) => boolean;
}

export type CueListItem = CueConfig;
